import { Formik } from 'formik';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  TabContent,
  TabPane,
  Input
} from 'reactstrap';
import * as Yup from 'yup';
import Loader from '../components/Loader';
import {
  departmentType,
  gender,
  penaltyOverrideOptions,
  source,
  visitReason
} from '../constants';
import {
  displayError,
  displaySuccess,
  isAge18orAbove,
  logAmplitudeEvent
} from '../utilities';
import {
  GET_ALL_HOSPITALS,
  getDxLtEdd,
  updatePatientDetails
} from '../queries';
import { fetchCustomerDetails } from '../utilities/fetchCustomerDetails';
import moment from 'moment';
import DynamicSelect from './DynamicSelect';
import { useQuery } from 'react-apollo';

const TABS = {
  CUSTOMER_DETAILS: 1
};

// eslint-disable-next-line react/prop-types
const ErrorMessage = ({ message }) => {
  return <div style={{ color: 'red' }}>{message}</div>;
};

const CustomerDetailsModal = ({ isOpen, toggle, userMPID }) => {
  const [selectedTab, setSelectedTab] = useState(TABS.CUSTOMER_DETAILS);
  const [loaderIcon, setLoaderIcon] = useState(true);
  const [customerDetails, setCustomerDetails] = useState([]);
  const [detailsLoaderIconVisible, setDetailsLoaderIconVisible] = useState(
    false
  );
  const [dxLtEdd, setDxLtEdd] = useState({});

  const { data: { hospitals = [] } = {} } = useQuery(GET_ALL_HOSPITALS, {
    variables: { showAll: false }
  });

  const formattedHospitals = hospitals.map(hospital => ({
    label: hospital.name,
    value: hospital.id
  }));

  const userDetails = JSON.parse(localStorage.getItem('user'));
  const userAllocatedHospital =
    userDetails.hospitals && userDetails.hospitals.length === 0
      ? formattedHospitals
      : hospitals
          .filter(hospital => userDetails.hospitals.includes(hospital.id))
          .map(hospital => ({
            value: hospital.id,
            label: hospital.name
          }));

  useEffect(() => {
    if (!isOpen) return;

    async function fetchData() {
      try {
        setLoaderIcon(true);
        setDetailsLoaderIconVisible(false);

        // fetch customer details
        const customerData = await fetchCustomerDetails(userMPID);
        if (customerData) {
          setCustomerDetails(customerData);
        }

        // fetch EDD
        const eddData = await getDxLtEdd(userMPID);
        setDxLtEdd(eddData.data.getDxAndLtEdd || {});

        setSelectedTab(TABS.CUSTOMER_DETAILS);

        // log event
        logAmplitudeEvent(
          'Customer_Details_Icon_Pressed',
          { userMPID, selectedTab: 'PATIENT DETAILS' },
          true
        );
      } catch (error) {
        console.error('Error fetching customer or EDD', error);
      } finally {
        setLoaderIcon(false);
      }
    }

    fetchData();
  }, [isOpen, toggle, userMPID]);

  // function to update customer's information.
  const handlePatientUpdate = values => {
    setDetailsLoaderIconVisible(true);
    const req_body = {
      input: {
        mpi: userMPID,
        registeredSiteCode: values.registeredCenter,
        gender: values.gender,
        dateOfBirth: values.dateOfBirth,
        firstName: values.name.trim(),
        lastName: values.lastName.trim(),
        emergencyContactName: values.emergencyContactName || '',
        emergencyContactMobile: values.emergencyContactMobile || '',
        email: values.email,
        edd:
          !['DX_APP'].includes(dxLtEdd.source) && values.edd
            ? moment(values.edd).format('YYYY-MM-DD')
            : '',
        departmentType: values.departmentType,
        visitReason: values.visitReason,
        source: values.source,
        remarks: values.remarks,
        vcReferral: values.vcReferral,
        pincode: values.pincode,
        hospitalId: values.hospitalId || null,
        penaltyOverride: values.penaltyOverride
      }
    };

    updatePatientDetails(req_body)
      .then(response => {
        if (
          response.data &&
          response.data.updatePatientDetails &&
          response.data.updatePatientDetails.message &&
          response.data.updatePatientDetails.message === 'Updated successfully'
        ) {
          setDetailsLoaderIconVisible(false);
          displaySuccess('Details updated successfully');
          logAmplitudeEvent(
            'Patient_Details_Updated_Successfully',
            {
              selectedTab: 'PATIENT DETAILS',
              userMPID: userMPID,
              userName: values.name.trim(),
              dateOfBirth: values.dateOfBirth,
              gender: values.gender,
              emergencyContactName: values.emergencyContactName,
              emergencyContactMobile: values.emergencyContactMobile,
              email: values.email,
              edd: moment(values.edd).format('YYYY-MM-DD') || ''
            },
            true
          );
        } else {
          setDetailsLoaderIconVisible(false);
          displayError('Update failed. Please try again');
          logAmplitudeEvent(
            'Patient_Details_Updation_Failed',
            {
              selectedTab: 'PATIENT DETAILS',
              userMPID: userMPID,
              userName: values.name.trim(),
              dateOfBirth: values.dateOfBirth,
              gender: values.gender,
              emergencyContactName: values.emergencyContactName,
              emergencyContactMobile: values.emergencyContactMobile,
              email: values.email,
              response: response.data,
              edd: moment(values.edd).format('YYYY-MM-DD') || ''
            },
            true
          );
        }
      })
      .catch(error => {
        setDetailsLoaderIconVisible(false);
        displayError(
          'We were unable to update the details. Please try again later'
        );
        logAmplitudeEvent(
          'Details_Updation_Failed',
          {
            selectedTab: 'PATIENT DETAILS',
            userMPID: userMPID,
            userName: values.name.trim(),
            dateOfBirth: values.dateOfBirth,
            gender: values.gender,
            emergencyContactName: values.emergencyContactName,
            emergencyContactMobile: values.emergencyContactMobile,
            email: values.email,
            edd: moment(values.edd).format('YYYY-MM-DD') || '',
            error: error
          },
          true
        );
      });
  };

  const patientDetailsSchema = Yup.object().shape({
    name: Yup.string()
      .min(3, 'Name must be minimum 3 characters !!!')
      .required('Required'),
    dateOfBirth: Yup.date().required('Please enter the date of birth'),
    mpid: Yup.string().required('Required'),
    registeredCenter: Yup.string().required('Required'),
    mobile: Yup.string().required('Required'),
    emergencyContactName: Yup.string().min(
      3,
      'Name must be minimum 3 characters !!!'
    ),
    gender: Yup.string()
      .required('Enter gender')
      .test('gender-validation', 'Invalid gender', function(value) {
        if (!value) return false;

        if (value.length < 4) {
          return this.createError({
            message: 'Gender must be minimum 4 characters'
          });
        }

        if (!['Male', 'Female'].includes(value)) {
          return this.createError({
            message: 'Gender must be either Male or Female'
          });
        }
        return true;
      }),

    emergencyContactMobile: Yup.string()
      .matches(/^[0-9]*$/, 'Please enter valid number!!!')
      .min(10, 'Please enter a valid 10 digit number !!!')
      .max(10, 'Please enter a valid 10 digit number !!!'),
    email: Yup.string()
      .nullable()
      .email('Invalid email')
      .required('Enter email'),
    edd: Yup.string()
      .nullable()
      .test('edd-date-range', 'Please enter a valid EDD', function(value) {
        if (!value) return true;

        const selectedDate = moment(value).startOf('day');
        const today = moment().startOf('day');
        const maxDate = today.clone().add(280, 'days');

        if (selectedDate.isBefore(today)) {
          return this.createError({ message: 'EDD cannot be in the past' });
        }
        if (selectedDate.isAfter(maxDate)) {
          return this.createError({
            message: 'EDD cannot be more than 280 days from today'
          });
        }
        return true;
      })
      .test(
        'female-min-age-18',
        'EDD can only be entered if the age is 18 years or above',
        function(value) {
          const { gender, dateOfBirth } = this.parent;
          if (!value) return true;

          if (gender !== 'Female') return true;

          if (!dateOfBirth) return true;

          const age = moment().diff(moment(dateOfBirth), 'years');

          return age >= 18;
        }
      ),
    departmentType: Yup.string(),
    visitReason: Yup.string(),
    source: Yup.string(),
    remarks: Yup.string().max(255, 'Only 255 characters are allowed'),
    vcReferral: Yup.string().max(100, 'Maximum 100 characters allowed'),
    pincode: Yup.number()
      .nullable()
      .transform((value, originalValue) => {
        const val = ~~originalValue;
        if (!val) {
          return null;
        }
        return Number(originalValue);
      })
      .test(
        'valid-pincode',
        'Invalid pincode',
        value =>
          value === null ||
          (Number.isInteger(value) && value >= 100000 && value <= 999999)
      ),
    hospitalId: Yup.number()
      .nullable()
      .when('edd', {
        is: edd => !!edd && !['DX_APP'].includes(dxLtEdd.source),
        then: schema =>
          schema.required('Hospital is required when EDD is selected'),
        otherwise: schema => schema.notRequired()
      })
  });

  return (
    <Modal size="lg" isOpen={isOpen} toggle={toggle}>
      <ModalHeader toggle={toggle}>Patient&#39;s Personal Details</ModalHeader>
      <ModalBody style={{ padding: '0px' }}>
        <TabContent activeTab={selectedTab}>
          <TabPane tabId={TABS.CUSTOMER_DETAILS}>
            {loaderIcon ? (
              <Loader />
            ) : selectedTab === TABS.CUSTOMER_DETAILS &&
              customerDetails !== [] ? (
              <Formik
                enableReinitialize={true}
                initialValues={{
                  name: customerDetails.first_name,
                  lastName: customerDetails.last_name || '',
                  dateOfBirth: customerDetails.date_of_birth,
                  mpid: customerDetails.mpid,
                  registeredCenter: customerDetails.registered_site_code,
                  mobile: customerDetails.mobile,
                  emergencyContactName: (
                    customerDetails.emergency_contact_name || ''
                  ).trim(),
                  gender: customerDetails.gender,
                  emergencyContactMobile: (
                    customerDetails.emergency_contact_mobile || ''
                  ).trim(),
                  email: customerDetails.email,
                  edd: dxLtEdd.edd || '',
                  departmentType: customerDetails.department_type || '',
                  visitReason: customerDetails.visit_reason || '',
                  source: customerDetails.source || '',
                  remarks: customerDetails.remarks || '',
                  vcReferral: customerDetails.vc_referral || '',
                  pincode:
                    customerDetails.address_pincode &&
                    customerDetails.address_pincode !== 'undefined'
                      ? customerDetails.address_pincode
                      : null,
                  hospitalId: ['DX_APP'].includes(dxLtEdd.source)
                    ? null
                    : (dxLtEdd.source === 'AMS' || dxLtEdd.source === 'LT') &&
                      customerDetails.hospital_id
                    ? customerDetails.hospital_id
                    : userAllocatedHospital.length === 1
                    ? userAllocatedHospital[0].value
                    : null,
                  penaltyOverride: Boolean(customerDetails.penaltyOverride)
                }}
                validationSchema={patientDetailsSchema}
                onSubmit={handlePatientUpdate}
              >
                {({
                  handleChange,
                  handleSubmit,
                  handleBlur,
                  values,
                  errors,
                  touched,
                  setFieldValue,
                  submitCount
                }) => (
                  <form onSubmit={handleSubmit}>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns:
                          'auto minmax(120px, 1fr) auto minmax(120px, 1fr)',
                        columnGap: '16px',
                        rowGap: '12px',
                        alignItems: 'center',
                        padding: '14px',
                        paddingTop: '26px'
                      }}
                    >
                      <label style={{ margin: '0px' }}>First Name:</label>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <input
                          type="text"
                          className="form-control"
                          onChange={handleChange('name')}
                          onBlur={handleBlur('name')}
                          value={values.name != null ? values.name : ''}
                        />
                        {errors.name && touched.name ? (
                          <ErrorMessage message={errors.name} />
                        ) : null}
                      </div>

                      <label style={{ margin: '0px' }}>Last Name:</label>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <input
                          type="text"
                          className="form-control"
                          onChange={handleChange('lastName')}
                          onBlur={handleBlur('lastName')}
                          value={values.lastName}
                        />
                      </div>

                      <label style={{ margin: '0px' }}>Date Of Birth:</label>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <input
                          type="date"
                          className="form-control"
                          name="dateOfBirth"
                          max={new Date().toISOString().split('T')[0]}
                          onChange={event => {
                            const dob = event.target.value;
                            setFieldValue('dateOfBirth', dob);
                          }}
                          value={values.dateOfBirth}
                        />
                        {errors.dateOfBirth && touched.dateOfBirth ? (
                          <ErrorMessage message={errors.dateOfBirth} />
                        ) : null}
                      </div>

                      <label style={{ margin: '0px' }}>Gender:</label>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <select
                          name="gender"
                          className="form-control"
                          value={values.gender}
                          onChange={event => {
                            const gender = event.target.value;
                            setFieldValue('gender', gender);
                            if (
                              gender === 'Male' ||
                              (gender === 'Female' &&
                                !isAge18orAbove(values.dateOfBirth))
                            ) {
                              setFieldValue('edd', '');
                              setFieldValue('hospitalId', null);
                            }
                          }}
                          onBlur={handleBlur}
                        >
                          <option value="">Select</option>

                          {gender.map((gen, i) => {
                            return (
                              <option gender={gen.value} name="gender" key={i}>
                                {gen.label}
                              </option>
                            );
                          })}
                        </select>
                        {errors.gender && touched.gender ? (
                          <ErrorMessage message={errors.gender} />
                        ) : null}
                      </div>

                      <label style={{ margin: '0px' }}>MPID:</label>
                      <input
                        type="text"
                        className="form-control"
                        value={values.mpid}
                        disabled
                      />

                      <label style={{ margin: '0px' }}>
                        Registered Center:
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        value={values.registeredCenter}
                        disabled
                      />

                      <label style={{ margin: '0px' }}>Mobile:</label>
                      <input
                        type="text"
                        className="form-control"
                        value={values.mobile}
                        disabled
                      />

                      <label style={{ margin: '0px' }}>
                        Emergency Contact Name:
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <input
                          type="text"
                          className="form-control"
                          onChange={handleChange('emergencyContactName')}
                          onBlur={handleBlur('emergencyContactName')}
                          value={values.emergencyContactName}
                        />
                        {errors.emergencyContactName &&
                        touched.emergencyContactName ? (
                          <ErrorMessage message={errors.emergencyContactName} />
                        ) : null}
                      </div>

                      <label style={{ margin: '0px' }}>Email:</label>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <input
                          type="text"
                          className="form-control"
                          onChange={handleChange('email')}
                          onBlur={handleBlur('email')}
                          value={values.email != null ? values.email : ''}
                        />
                        {errors.email && touched.email ? (
                          <ErrorMessage message={errors.email} />
                        ) : null}
                      </div>

                      <label style={{ margin: '0px' }}>
                        Emergency Contact Number:
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <input
                          type="text"
                          className="form-control"
                          onChange={handleChange('emergencyContactMobile')}
                          onBlur={handleBlur('emergencyContactMobile')}
                          value={
                            values.emergencyContactMobile != null
                              ? values.emergencyContactMobile
                              : ''
                          }
                        />
                        {errors.emergencyContactMobile &&
                        touched.emergencyContactMobile ? (
                          <ErrorMessage
                            message={errors.emergencyContactMobile}
                          />
                        ) : null}
                      </div>

                      <label htmlFor="edd" style={{ margin: '0px' }}>
                        EDD:
                      </label>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          position: 'relative'
                        }}
                      >
                        <Input
                          id="edd"
                          name="edd"
                          placeholder="EDD"
                          type="date"
                          value={values.edd}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          disabled={
                            dxLtEdd.source === 'DX_APP' ||
                            values.gender === 'Male' ||
                            (values.gender === 'Female' &&
                              moment().diff(
                                moment(values.dateOfBirth),
                                'years'
                              ) < 18)
                          }
                        />
                        {errors.edd ? (
                          <span style={{ color: 'red' }}>{errors.edd}</span>
                        ) : null}

                        {dxLtEdd.edd ? (
                          <span
                            style={{
                              position: 'absolute',
                              right: 35,
                              fontSize: '0.8rem',
                              color: '#007BFF',
                              top: '10px'
                            }}
                          >
                            Source:{' '}
                            {dxLtEdd.source === 'DX_APP'
                              ? dxLtEdd.source
                              : dxLtEdd.source}
                          </span>
                        ) : null}
                      </div>

                      <label htmlFor="edd" style={{ margin: '0px' }}>
                        Hospital:
                        {values.edd &&
                          (!dxLtEdd.source ||
                            dxLtEdd.source === 'AMS' ||
                            dxLtEdd.source === 'LT') && (
                            <span style={{ fontSize: '1em', color: 'red' }}>
                              {' '}
                              *
                            </span>
                          )}
                      </label>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <DynamicSelect
                          name="hospitalId"
                          value={values.hospitalId}
                          options={userAllocatedHospital}
                          onChange={(field, value) =>
                            setFieldValue(field, value)
                          }
                          width="100%"
                          isDisabled={
                            !values.edd ||
                            !!errors.edd ||
                            ['DX_APP'].includes(dxLtEdd.source) ||
                            values.gender === 'Male' ||
                            (values.gender === 'Female' &&
                              moment().diff(
                                moment(values.dateOfBirth),
                                'years'
                              ) < 18)
                          }
                        />
                        {errors.hospitalId && submitCount > 0 ? (
                          <ErrorMessage message={errors.hospitalId} />
                        ) : null}
                      </div>

                      <label style={{ margin: '0px' }}>
                        Penalty Exemption:
                      </label>
                      <DynamicSelect
                        name="penaltyOverride"
                        value={values.penaltyOverride}
                        options={penaltyOverrideOptions}
                        onChange={(field, value) => setFieldValue(field, value)}
                        width="100%"
                        isClearable={false}
                      />
                    </div>
                    <hr style={{ margin: '0px' }} />
                    <div
                      style={{
                        fontSize: '1.25rem',
                        fontWidth: 'bold',
                        padding: '0.5rem 1rem'
                      }}
                    >
                      Patient&#39;s Welcome Call Details
                    </div>
                    <hr style={{ margin: '0px' }} />
                    <ModalBody>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns:
                            'repeat(auto-fit, minmax(350px, 1fr))',
                          gap: '12px',
                          justifyContent: 'space-between'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: '6px'
                          }}
                        >
                          <label
                            htmlFor="departmentType"
                            style={{ margin: '0px' }}
                          >
                            Type:
                          </label>
                          <DynamicSelect
                            name="departmentType"
                            value={values.departmentType}
                            options={departmentType}
                            onChange={(field, value) => {
                              setFieldValue(field, value);
                              if (!value) {
                                setFieldValue('visitReason', '');
                              }
                            }}
                          />
                        </div>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            justifyContent: 'space-between'
                          }}
                        >
                          <label
                            htmlFor="visitReason"
                            style={{ margin: '0px' }}
                          >
                            Visit Reason:
                          </label>

                          <DynamicSelect
                            name="visitReason"
                            value={values.visitReason}
                            options={
                              (values.departmentType &&
                                visitReason.find(f => {
                                  return values.departmentType === f.type;
                                }).options) ||
                              []
                            }
                            onChange={(field, value) =>
                              setFieldValue(field, value)
                            }
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            justifyContent: 'space-between'
                          }}
                        >
                          <label htmlFor="source" style={{ margin: '0px' }}>
                            Source:
                          </label>
                          <DynamicSelect
                            name="source"
                            value={values.source}
                            options={source}
                            onChange={(field, value) => {
                              setFieldValue(field, value);
                              if (
                                value !==
                                'Doctor Referral - Visiting Consultant'
                              ) {
                                setFieldValue('vcReferral', '');
                              }
                            }}
                          />
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            justifyContent: 'space-between'
                          }}
                        >
                          <label htmlFor="pincode" style={{ margin: '0px' }}>
                            Pincode:
                          </label>

                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '70%'
                            }}
                          >
                            <Input
                              type="number"
                              value={values.pincode}
                              onChange={e => {
                                const value = e.target.value;
                                setFieldValue(
                                  'pincode',
                                  value === '' ? null : Number(value)
                                );
                              }}
                            />
                            {touched.pincode && errors.pincode ? (
                              <span style={{ color: 'red' }}>
                                {errors.pincode}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '6px',
                            justifyContent: 'space-between'
                          }}
                        >
                          <label
                            htmlFor="remarks"
                            style={{ margin: '0px', marginTop: '8px' }}
                          >
                            Remarks:
                          </label>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '70%'
                            }}
                          >
                            <Input
                              style={{ width: '100%' }}
                              type="textarea"
                              value={values.remarks}
                              onChange={e => {
                                setFieldValue('remarks', e.target.value);
                              }}
                            />
                            {touched.remarks && errors.remarks ? (
                              <span style={{ color: 'red' }}>
                                {errors.remarks}
                              </span>
                            ) : null}
                          </div>
                        </div>
                        {values.source ===
                          'Doctor Referral - Visiting Consultant' && (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              justifyContent: 'space-between'
                            }}
                          >
                            <label
                              htmlFor="vcReferral"
                              style={{ margin: '0px' }}
                            >
                              VC Referral:
                            </label>

                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                width: '70%'
                              }}
                            >
                              <Input
                                style={{ width: '100%' }}
                                type="text"
                                value={values.vcReferral}
                                onChange={e => {
                                  setFieldValue('vcReferral', e.target.value);
                                }}
                              />
                              {touched.vcReferral && errors.vcReferral ? (
                                <span style={{ color: 'red' }}>
                                  {errors.vcReferral}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </div>
                    </ModalBody>

                    {detailsLoaderIconVisible ? (
                      <div
                        style={{
                          margin: 12,
                          marginRight: 15,
                          float: 'right'
                        }}
                      >
                        <Loader inline size={5} />
                      </div>
                    ) : (
                      <Button
                        color="primary"
                        style={{
                          margin: 12,
                          marginRight: 15,
                          float: 'right'
                        }}
                        type="submit"
                      >
                        UPDATE DETAILS
                      </Button>
                    )}
                  </form>
                )}
              </Formik>
            ) : null}
          </TabPane>
        </TabContent>
      </ModalBody>
    </Modal>
  );
};

CustomerDetailsModal.propTypes = {
  userMPID: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired
};

export default CustomerDetailsModal;
